import React, { useEffect, useState } from "react";
import type { FormProps } from "antd";
import { Button, Form, Input } from "antd";
import { Link } from "react-router-dom";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "src/components/language-switcher";
import { useAuth } from "src/hooks/auth.hook";
import { routes } from "src/data/routes";
import { useAnalytics } from "src/hooks/analytics.hook";
import { usePageTracking } from "src/hooks/tracking-page.hook";

export default function Login() {
  type FieldType = {
    email?: string;
    password?: string;
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();
  const [errorCode, setErrorCode] = useState<any>();
  const { t } = useTranslation("login");
  const { trackPage } = usePageTracking();

  const { login } = useAuth();
  const { track, onlogin } = useAnalytics();

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    try {
      setLoading(true);
      await login({
        email: values?.email || "",
        password: values.password || "",
      });
      onlogin();
    } catch (error) {
      if (error instanceof AxiosError) {
        setErrorCode(error?.response?.data?.message || error?.message || "");
        return setError(error?.response?.data?.message || error?.message || "");
      }
      if (error instanceof Error) {
        return setError(error?.message || "");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (error) {
      track("Error", {
        page_current: "sign_in",
        error_message: error,
        error_code: errorCode,
      });
    }
  }, [error]);

  useEffect(() => {
    trackPage();
  }, []);

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = async (
    values
  ) => {
    track("Error", {
      page_current: "password_reset_input",
      error_message: values?.errorFields[0]?.errors[0],
      error_code: "passwordTitleErrorMessage",
    });
  };
  return (
    <div className="bg-dark bg-login w-full h-full flex flex-col justify-start items-center">
      <div className="flex justify-end items-center w-full pt-4 pr-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full h-full flex  justify-center items-center">
        <div className="bg-white w-[20rem] md:w-[30rem] p-8 rounded-sm shadow">
          <img src="/logo-white.svg" alt="" className="w-auto h-[40px] mb-8" />
          <h2 className="text-4xl font-semibold">{t("title")}</h2>
          <h2 className="text-sm mb-8">{t("subtitle")}</h2>
          <div className="w-full flex justify-center">
            <Form
              name="basic"
              layout="vertical"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              size="large"
              autoComplete="off"
              className="w-full"
            >
              <Form.Item<FieldType>
                label={<label className="text-lg">{t("emailTitle")}</label>}
                name="email"
                rules={[
                  { required: true, message: t("emailTitleErrorMessage") },
                ]}
              >
                <Input disabled={loading} />
              </Form.Item>

              <Form.Item<FieldType>
                label={<label className="text-lg">{t("passwordTitle")}</label>}
                name="password"
                rules={[
                  { required: true, message: t("passwordTitleErrorMessage") },
                ]}
              >
                <Input.Password disabled={loading} />
              </Form.Item>
              <Form.Item>
                <Button
                  loading={loading}
                  htmlType="submit"
                  size="large"
                  className="w-full mt-8 bg-dark text-white "
                >
                  {t("loginBtnTitle")}
                </Button>
                <h6 className="text-red-600 mt-1">{error}</h6>
                <Link
                  to={routes.FORGOT_PASSWORD}
                  className="cursor-pointer underline hover:underline   text-dark hover:text-blue-700 mt-1"
                >
                  {t("forgotPasswordLinkTitle")}
                </Link>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
